import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

export type ThemeId = 'park-model' | 'toyworks' | 'afterglow';

export interface ThemeDefinition {
  id: ThemeId;
  label: string;
  description: string;
  colors: {
    background: number;
    fog: number;
    ground: number;
    groundSecondary: number;
    base: number;
    paving: number;
    scenery: number;
    hedge: number;
    trunk: number;
    rock: number;
    environmentAccent: number;
    practical: number;
    skyTop: number;
    skyHorizon: number;
  };
  lighting: {
    hemisphereSky: number;
    hemisphereGround: number;
    hemisphereIntensity: number;
    sunColor: number;
    sunIntensity: number;
    fillColor: number;
    fillIntensity: number;
    practicalIntensity: number;
    exposure: number;
  };
  fog: { near: number; far: number };
  postEffects: {
    fog: boolean;
    bloomCandidate: boolean;
    outlineCandidate: boolean;
  };
}

export const THEMES: Record<ThemeId, ThemeDefinition> = {
  'park-model': {
    id: 'park-model',
    label: 'Park Model',
    description: 'Default architectural miniature with matte surfaces, daylight clarity, and restrained accents.',
    colors: {
      background: 0xb8d4d7,
      fog: 0xb8d4d7,
      ground: 0xc8bfac,
      groundSecondary: 0x789b68,
      base: 0x303b48,
      paving: 0xd5cbb8,
      scenery: 0x376f50,
      hedge: 0x5f9470,
      trunk: 0x7b5b43,
      rock: 0x8b918e,
      environmentAccent: 0xa9604b,
      practical: 0xffd694,
      skyTop: 0x72a7cb,
      skyHorizon: 0xcde8e4,
    },
    lighting: {
      hemisphereSky: 0xf2fbff,
      hemisphereGround: 0x314035,
      hemisphereIntensity: 0.52,
      sunColor: 0xfff2d6,
      sunIntensity: 2.75,
      fillColor: 0x9fc7db,
      fillIntensity: 0.16,
      practicalIntensity: 0,
      exposure: 0.94,
    },
    fog: { near: 62, far: 122 },
    postEffects: { fog: true, bloomCandidate: false, outlineCandidate: true },
  },
  toyworks: {
    id: 'toyworks',
    label: 'Toyworks',
    description: 'Bright molded-construction palette using the same geometry and material slots as Park Model.',
    colors: {
      background: 0x8bd3e8,
      fog: 0xa8dce7,
      ground: 0xc89f52,
      groundSecondary: 0x759746,
      base: 0x756442,
      paving: 0xe0bd69,
      scenery: 0x237746,
      hedge: 0x5d943e,
      trunk: 0x9a643e,
      rock: 0x8d8a78,
      environmentAccent: 0xd86a35,
      practical: 0xffe1a2,
      skyTop: 0x58b9dc,
      skyHorizon: 0xc8eff0,
    },
    lighting: {
      hemisphereSky: 0xf5fdff,
      hemisphereGround: 0x35513a,
      hemisphereIntensity: 0.56,
      sunColor: 0xfff2cf,
      sunIntensity: 3,
      fillColor: 0x93d7df,
      fillIntensity: 0.18,
      practicalIntensity: 0,
      exposure: 1,
    },
    fog: { near: 68, far: 132 },
    postEffects: { fog: true, bloomCandidate: false, outlineCandidate: true },
  },
  afterglow: {
    id: 'afterglow',
    label: 'Afterglow',
    description: 'Blue-hour environment with warm station practicals and bounded emissive accents.',
    colors: {
      background: 0x456888,
      fog: 0x486c8b,
      ground: 0x507363,
      groundSecondary: 0x315f4d,
      base: 0x182330,
      paving: 0x52666a,
      scenery: 0x3d755a,
      hedge: 0x487b62,
      trunk: 0x604a42,
      rock: 0x68737b,
      environmentAccent: 0xc75b47,
      practical: 0xffba61,
      skyTop: 0x172842,
      skyHorizon: 0x547da0,
    },
    lighting: {
      hemisphereSky: 0x91add0,
      hemisphereGround: 0x25313b,
      hemisphereIntensity: 0.54,
      sunColor: 0xc4d3e8,
      sunIntensity: 1.85,
      fillColor: 0x5d7fb5,
      fillIntensity: 0.46,
      practicalIntensity: 6,
      exposure: 1.08,
    },
    fog: { near: 72, far: 135 },
    postEffects: { fog: true, bloomCandidate: true, outlineCandidate: true },
  },
};

export interface SharedAssetKit {
  geometries: {
    base: THREE.BufferGeometry;
    ground: THREE.BufferGeometry;
    paving: THREE.BufferGeometry;
    tie: THREE.BufferGeometry;
    support: THREE.BufferGeometry;
    supportFooting: THREE.BufferGeometry;
    supportCap: THREE.BufferGeometry;
    train: THREE.BufferGeometry;
    trainSeat: THREE.BufferGeometry;
    trainWheel: THREE.BufferGeometry;
    station: THREE.BufferGeometry;
    stationRoof: THREE.BufferGeometry;
    stationColumn: THREE.BufferGeometry;
    tree: THREE.BufferGeometry;
    treeRound: THREE.BufferGeometry;
    trunk: THREE.BufferGeometry;
    shrub: THREE.BufferGeometry;
    rock: THREE.BufferGeometry;
    hedge: THREE.BufferGeometry;
  };
  materials: {
    track: THREE.MeshStandardMaterial;
    tie: THREE.MeshStandardMaterial;
    support: THREE.MeshStandardMaterial;
    train: THREE.MeshStandardMaterial;
    station: THREE.MeshStandardMaterial;
    stationRoof: THREE.MeshStandardMaterial;
    stationDark: THREE.MeshStandardMaterial;
    base: THREE.MeshStandardMaterial;
    ground: THREE.MeshStandardMaterial;
    groundSecondary: THREE.MeshStandardMaterial;
    paving: THREE.MeshStandardMaterial;
    scenery: THREE.MeshStandardMaterial;
    hedge: THREE.MeshStandardMaterial;
    trunk: THREE.MeshStandardMaterial;
    rock: THREE.MeshStandardMaterial;
    environmentAccent: THREE.MeshStandardMaterial;
    practical: THREE.MeshStandardMaterial;
  };
  sharedGeometries: Set<THREE.BufferGeometry>;
  sharedMaterials: Set<THREE.Material>;
}

function standardMaterial(roughness: number, metalness = 0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ roughness, metalness });
}

function physicalMaterial(roughness: number, metalness: number, clearcoat: number): THREE.MeshPhysicalMaterial {
  return new THREE.MeshPhysicalMaterial({ roughness, metalness, clearcoat, clearcoatRoughness: 0.28 });
}

export function createSharedAssetKit(): SharedAssetKit {
  const geometries = {
    base: new RoundedBoxGeometry(72, 3.4, 64, 4, 1.4),
    ground: new RoundedBoxGeometry(68.5, 0.9, 60.5, 4, 1.1),
    paving: new THREE.BoxGeometry(1.72, 0.08, 1.72),
    tie: new RoundedBoxGeometry(1.75, 0.13, 0.24, 2, 0.05),
    support: new THREE.CylinderGeometry(0.13, 0.2, 1, 8),
    supportFooting: new RoundedBoxGeometry(0.8, 0.22, 0.8, 2, 0.08),
    supportCap: new RoundedBoxGeometry(1.5, 0.22, 0.34, 2, 0.06),
    train: new RoundedBoxGeometry(1.85, 0.78, 2.65, 4, 0.2),
    trainSeat: new RoundedBoxGeometry(0.68, 0.62, 0.55, 3, 0.13),
    trainWheel: new THREE.CylinderGeometry(0.19, 0.19, 0.18, 12),
    station: new RoundedBoxGeometry(1, 1, 1, 3, 0.12),
    stationRoof: new RoundedBoxGeometry(1, 1, 1, 4, 0.2),
    stationColumn: new RoundedBoxGeometry(0.45, 1, 0.45, 3, 0.1),
    tree: new THREE.ConeGeometry(0.82, 2.5, 8),
    treeRound: new THREE.DodecahedronGeometry(0.9, 0),
    trunk: new THREE.CylinderGeometry(0.15, 0.2, 1, 7),
    shrub: new THREE.DodecahedronGeometry(0.48, 0),
    rock: new THREE.DodecahedronGeometry(0.72, 0),
    hedge: new RoundedBoxGeometry(1, 1, 1, 2, 0.16),
  };
  const materials = {
    track: physicalMaterial(0.28, 0.58, 0.26),
    tie: physicalMaterial(0.38, 0.62, 0.12),
    support: physicalMaterial(0.46, 0.52, 0.08),
    train: physicalMaterial(0.26, 0.2, 0.55),
    station: standardMaterial(0.82),
    stationRoof: physicalMaterial(0.42, 0.22, 0.34),
    stationDark: standardMaterial(0.5, 0.35),
    base: standardMaterial(0.78, 0.08),
    ground: standardMaterial(0.88),
    groundSecondary: standardMaterial(0.93),
    paving: standardMaterial(0.9),
    scenery: standardMaterial(0.88),
    hedge: standardMaterial(0.94),
    trunk: standardMaterial(0.86),
    rock: standardMaterial(0.96),
    environmentAccent: physicalMaterial(0.48, 0.14, 0.18),
    practical: new THREE.MeshStandardMaterial({ roughness: 0.35, emissiveIntensity: 0 }),
  };
  // Coaster appearance is intentionally independent from environment themes.
  // Creators will customize these fixed slots through a separate appearance layer.
  materials.track.color.setHex(0x2e5eaa);
  materials.tie.color.setHex(0x283744);
  materials.support.color.setHex(0x4d5965);
  materials.train.color.setHex(0xc84e3f);
  materials.station.color.setHex(0xe9dfcd);
  materials.stationRoof.color.setHex(0x385768);
  materials.stationDark.color.setHex(0x25313a);
  return {
    geometries,
    materials,
    sharedGeometries: new Set(Object.values(geometries)),
    sharedMaterials: new Set(Object.values(materials)),
  };
}

function color(material: THREE.MeshStandardMaterial, value: number): void {
  material.color.setHex(value);
}

export function applyThemeToAssetKit(kit: SharedAssetKit, theme: ThemeDefinition): void {
  // Themes style the environment. They do not overwrite creator-owned coaster,
  // train, support, or station appearance slots.
  color(kit.materials.ground, theme.colors.ground);
  color(kit.materials.groundSecondary, theme.colors.groundSecondary);
  color(kit.materials.base, theme.colors.base);
  color(kit.materials.paving, theme.colors.paving);
  color(kit.materials.scenery, theme.colors.scenery);
  color(kit.materials.hedge, theme.colors.hedge);
  color(kit.materials.trunk, theme.colors.trunk);
  color(kit.materials.rock, theme.colors.rock);
  color(kit.materials.environmentAccent, theme.colors.environmentAccent);
  color(kit.materials.practical, theme.colors.practical);
  kit.materials.practical.emissive.setHex(theme.colors.practical);
  kit.materials.practical.emissiveIntensity = theme.lighting.practicalIntensity > 0 ? 2.8 : 0.12;
}

export interface ThemeLightingRig {
  hemisphere: THREE.HemisphereLight;
  sun: THREE.DirectionalLight;
  fill: THREE.DirectionalLight;
  practicals: THREE.PointLight[];
  skyMaterial: THREE.ShaderMaterial;
}

export function applyThemeToWorld(
  theme: ThemeDefinition,
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  rig: ThemeLightingRig,
): void {
  scene.background = new THREE.Color(theme.colors.background);
  scene.fog = theme.postEffects.fog ? new THREE.Fog(theme.colors.fog, theme.fog.near, theme.fog.far) : null;
  rig.hemisphere.color.setHex(theme.lighting.hemisphereSky);
  rig.hemisphere.groundColor.setHex(theme.lighting.hemisphereGround);
  rig.hemisphere.intensity = theme.lighting.hemisphereIntensity;
  rig.sun.color.setHex(theme.lighting.sunColor);
  rig.sun.intensity = theme.lighting.sunIntensity;
  rig.fill.color.setHex(theme.lighting.fillColor);
  rig.fill.intensity = theme.lighting.fillIntensity;
  rig.practicals.forEach((light) => {
    light.color.setHex(theme.colors.practical);
    light.intensity = theme.lighting.practicalIntensity;
  });
  (rig.skyMaterial.uniforms.topColor.value as THREE.Color).setHex(theme.colors.skyTop);
  (rig.skyMaterial.uniforms.horizonColor.value as THREE.Color).setHex(theme.colors.skyHorizon);
  renderer.toneMappingExposure = theme.lighting.exposure;
}

export function themeEvidence(theme: ThemeDefinition): Record<string, string | number | boolean> {
  return {
    id: theme.id,
    label: theme.label,
    fogEnabled: theme.postEffects.fog,
    bloomCandidate: theme.postEffects.bloomCandidate,
    bloomApplied: false,
    outlineCandidate: theme.postEffects.outlineCandidate,
    outlineApplied: false,
    sunIntensity: theme.lighting.sunIntensity,
    hemisphereIntensity: theme.lighting.hemisphereIntensity,
    practicalIntensity: theme.lighting.practicalIntensity,
    exposure: theme.lighting.exposure,
    geometryPolicy: 'shared-across-themes',
    materialSlotPolicy: 'shared-instances-environment-only-reparameterization',
    coasterAppearancePolicy: 'independent-creator-customization',
    trackEmissiveApplied: false,
  };
}
